import React, { useState, useEffect } from 'react';
import { YokaiOutput } from '../../types/yokai';
import './YokaiDisplay.css';

interface YokaiDisplayProps {
  yokaiData: YokaiOutput | null;
  isLoading: boolean;
  error: string | null;
  progress: number;
  onImageClick: (url: string, index: number) => void;
}

const YokaiDisplay: React.FC<YokaiDisplayProps> = ({ yokaiData, isLoading, error, progress, onImageClick }) => {
  const [isImageRevealed, setIsImageRevealed] = useState(false);

  // 新しい妖怪が生成されたら画像を隠す
  useEffect(() => {
    setIsImageRevealed(false);
  }, [yokaiData?.name]);

  const handleRevealImage = () => {
    setIsImageRevealed(true);
  };
  if (isLoading) {
    return (
      <div className="yokai-display">
        <div className="yokai-display-container">
          <h2 className="yokai-display-title">妖怪データベース</h2>
          <div className="loading-container">
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="progress-text">{progress}% - 妖怪を探しています...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="yokai-display">
        <div className="yokai-display-container">
          <h2 className="yokai-display-title">妖怪データベース</h2>
          <div className="error-container">
            <h3>エラーが発生しました</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!yokaiData) {
    return (
      <div className="yokai-display">
        <div className="yokai-display-container">
          <h2 className="yokai-display-title">妖怪データベース</h2>
          <div className="empty-container"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="yokai-display">
      <div className="yokai-display-container">
        <h2 className="yokai-display-title">妖怪データベース</h2>
        
        {/* 画像セクション */}
        <div className="yokai-images">
          {/* 画像ローディング中 */}
          {yokaiData.imagesLoading && (
            <div className="images-loading-container">
              <div className="images-loading-spinner"></div>
            </div>
          )}
          
          {/* 画像生成エラー */}
          {yokaiData.imageError && (
            <div className="images-error-container">
              <p className="images-error-title">⚠️ 画像生成に失敗しました</p>
              <p className="images-error-message">{yokaiData.imageError}</p>
            </div>
          )}
          
          {/* 画像表示 */}
          {yokaiData.images && yokaiData.images.length > 0 && (
            <div className="single-image-container">
              <div 
                className="image-item"
                onClick={() => onImageClick(yokaiData.images[0].url, 0)}
              >
                <img
                  src={yokaiData.images[0].url}
                  alt={`${yokaiData.name}の姿`}
                  className={`yokai-image ${isImageRevealed ? 'revealed' : 'blurred'}`}
                  onLoad={() => console.log('画像読み込み完了')}
                  onError={() => console.error('画像読み込みエラー')}
                />
                {!isImageRevealed && (
                  <div className="image-blur-overlay">
                    <button 
                      className="reveal-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRevealImage();
                      }}
                    >
                      👻 妖怪を見る
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="yokai-data">
          {/* 1行目：妖怪名、口癖 */}
          <div className="data-row">
            <div className="data-field">
              <label className="data-label">妖怪名</label>
              <div className="data-content name-content">
                {yokaiData.name ? yokaiData.name : (
                  <div className="field-loading">
                    <div className="field-loading-spinner"></div>
                    <span>生成中...</span>
                  </div>
                )}
              </div>
            </div>
            <div className="data-field">
              <label className="data-label">口癖</label>
              <div className="data-content catchphrase-content">
                {yokaiData.catchphrase ? `「${yokaiData.catchphrase}」` : (
                  <div className="field-loading">
                    <div className="field-loading-spinner"></div>
                    <span>生成中...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2行目：説明 */}
          <div className="data-field">
            <label className="data-label">説明</label>
            <div className="data-content description-content">
              {yokaiData.description ? yokaiData.description : (
                <div className="field-loading">
                  <div className="field-loading-spinner"></div>
                  <span>生成中...</span>
                </div>
              )}
            </div>
          </div>

          {/* 3行目：好きなもの、苦手なもの */}
          <div className="data-row">
            <div className="data-field">
              <label className="data-label">好きなもの</label>
              <div className="data-content likes-content">
                {yokaiData.likes ? yokaiData.likes : (
                  <div className="field-loading">
                    <div className="field-loading-spinner"></div>
                    <span>生成中...</span>
                  </div>
                )}
              </div>
            </div>
            <div className="data-field">
              <label className="data-label">苦手なもの</label>
              <div className="data-content dislikes-content">
                {yokaiData.dislikes ? yokaiData.dislikes : (
                  <div className="field-loading">
                    <div className="field-loading-spinner"></div>
                    <span>生成中...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YokaiDisplay;